import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface BotRow {
  id: string;
  userId: string;
  username: string;
  telegramUserId: number;
  packageKey: string;
  packageFee: number;
  lockedBalance: number;
  rewardAmount: number;
  defaultRewardAmount: number | null;
  opportunityInterval: number;
  status: string;
  consecutiveMissed: number;
  perfectDaysCount: number;
  perfectBonusTotal: number;
  giftsSentThisMonth: number;
  startedAt: string;
  endsAt: string;
  daysRemaining: number;
}

interface OppRow {
  id: string;
  username: string;
  packageKey: string;
  status: string;
  rewardAmount: number;
  opensAt: string;
  expiresAt: string;
  caughtAt: string | null;
  rewardedAt: string | null;
  signalPair: string | null;
  signalDirection: string | null;
  signalEntry: string | null;
  signalTp: string | null;
  signalSl: string | null;
  signalExchange: string | null;
}

interface SignalRow {
  id: string;
  pair: string;
  direction: string;
  entry: string;
  tp: string;
  sl: string;
  exchange: string | null;
  packageKey: string | null;
  isActive: boolean;
  expiresAt: string;
  appliedCount: number;
  createdAt: string;
}

const SIGNAL_DURATIONS = [1, 4, 12, 24, 72];

interface UserHit {
  id: string;
  telegramUserId: number;
  username: string | null;
  firstName: string;
  lastName: string | null;
}

interface AiTraderStats {
  activeBots: number;
  totalBots: number;
  totalLocked: number;
  totalRewardsDistributed: number;
  totalGiftsSent: number;
  totalGiftsAmount: number;
}

type SubView = 'bots' | 'opportunities' | 'signal';
type BotFilter = 'all' | 'active' | 'pause' | 'expired';
type OppFilter = 'all' | 'open' | 'reward_pending' | 'missed' | 'rewarded' | 'lost';

const PKG_COLORS: Record<string, string> = {
  new_trader: '#10b981',
  adventurer: '#3b82f6',
  prof_trader: '#8b5cf6',
  elite_trader: '#f59e0b',
};

const PKG_NAMES: Record<string, string> = {
  new_trader: 'New Trader',
  adventurer: 'Adventurer',
  prof_trader: 'Prof Trader',
  elite_trader: 'Elite Trader',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  pause: '#f59e0b',
  expired: '#ef4444',
  open: '#3b82f6',
  reward_pending: '#f59e0b',
  missed: '#ef4444',
  rewarded: '#10b981',
  lost: '#6b7280',
};

const cardStyle = { background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '14px', border: '1px solid rgba(255,255,255,0.06)' } as React.CSSProperties;
const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const } as React.CSSProperties;

export function AdminAiTrader() {
  const [stats, setStats] = useState<AiTraderStats | null>(null);
  const [bots, setBots] = useState<BotRow[]>([]);
  const [opps, setOpps] = useState<OppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [subView, setSubView] = useState<SubView>('signal');
  const [signals, setSignals] = useState<SignalRow[]>([]);
  const [sigPackage, setSigPackage] = useState('');
  const [sigDuration, setSigDuration] = useState(4);
  const [incomeEdit, setIncomeEdit] = useState<Record<string, string>>({});
  const [savingIncome, setSavingIncome] = useState<string | null>(null);
  const [botFilter, setBotFilter] = useState<BotFilter>('all');
  const [oppFilter, setOppFilter] = useState<OppFilter>('all');

  const [modUserId, setModUserId] = useState('');
  const [modUserQuery, setModUserQuery] = useState('');
  const [userHits, setUserHits] = useState<UserHit[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserHit | null>(null);
  const [modAmount, setModAmount] = useState('');
  const [modReason, setModReason] = useState('');
  const [modifying, setModifying] = useState(false);

  const [sigOppId, setSigOppId] = useState('');
  const [sigPair, setSigPair] = useState('');
  const [sigDirection, setSigDirection] = useState('LONG');
  const [sigEntry, setSigEntry] = useState('');
  const [sigTp, setSigTp] = useState('');
  const [sigSl, setSigSl] = useState('');
  const [sigExchange, setSigExchange] = useState('');
  const [sendingSignal, setSendingSignal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, botsRes, oppsRes, signalsRes] = await Promise.all([
        api.getAdminAiTraderStats(),
        api.getAdminAiTraderBots(botFilter === 'all' ? undefined : botFilter),
        api.getAdminAiTraderOpportunities(undefined, oppFilter === 'all' ? undefined : oppFilter),
        api.getAdminAiTraderSignals(),
      ]);
      setStats(statsRes.data.data);
      setBots(botsRes.data.bots || []);
      setOpps(oppsRes.data.opportunities || []);
      setSignals(signalsRes.data.signals || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [botFilter, oppFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const q = modUserQuery.trim().replace(/^@/, '');
    if (selectedUser || q.length < 2) { setUserHits([]); return; }
    const id = setTimeout(() => {
      api.getAdminUsers({ page: 1, limit: 6, search: q })
        .then(res => setUserHits(res.data.users || []))
        .catch(() => setUserHits([]));
    }, 400);
    return () => clearTimeout(id);
  }, [modUserQuery, selectedUser]);

  const pickUser = (u: UserHit) => {
    setSelectedUser(u);
    setModUserId(u.id);
    setModUserQuery(u.username ? `@${u.username}` : u.firstName);
    setUserHits([]);
  };

  const errMsg = (e: any) => e.response?.data?.error?.message || e.response?.data?.message || 'Failed';

  const handleModifyBalance = async () => {
    const target = modUserId || modUserQuery.trim();
    if (!target || !modAmount || !modReason || modifying) return;
    setModifying(true);
    try {
      const res = await api.modifyUserBotBalance(target, Number(modAmount), modReason);
      const u = res.data.user;
      alert(`Bot Balance updated for ${u?.username ? '@' + u.username : u?.firstName || target}. New balance: $${Number(res.data.newBalance).toFixed(2)}`);
      setModUserId(''); setModUserQuery(''); setSelectedUser(null); setModAmount(''); setModReason('');
      fetchData();
    } catch (e: any) {
      alert(errMsg(e));
    } finally {
      setModifying(false);
    }
  };

  const startSignal = (opp: OppRow) => {
    setSigOppId(opp.id);
    setSigPair(opp.signalPair || '');
    setSigDirection(opp.signalDirection || 'LONG');
    setSigEntry(opp.signalEntry || '');
    setSigTp(opp.signalTp || '');
    setSigSl(opp.signalSl || '');
    setSigExchange(opp.signalExchange || '');
    setSubView('signal');
    window.scrollTo(0, 0);
  };

  const resetSignalForm = () => {
    setSigOppId(''); setSigPair(''); setSigEntry(''); setSigTp(''); setSigSl(''); setSigExchange('');
  };

  const handleAddSignal = async () => {
    if (!sigPair || !sigEntry || !sigTp || !sigSl || sendingSignal) return;
    setSendingSignal(true);
    try {
      if (sigOppId) {
        await api.addAiTraderSignal(sigOppId.trim(), {
          pair: sigPair, direction: sigDirection, entry: sigEntry,
          tp: sigTp, sl: sigSl, exchange: sigExchange,
        });
        alert('Signal saved for this opportunity.');
      } else {
        const res = await api.publishAiTraderSignal({
          pair: sigPair, direction: sigDirection, entry: sigEntry, tp: sigTp, sl: sigSl,
          exchange: sigExchange || undefined, packageKey: sigPackage || null, durationHours: sigDuration,
        });
        alert(`Signal published for ${sigDuration}h. Shown now in ${res.data.appliedNow} open opportunities and in every new one until it expires.`);
      }
      resetSignalForm();
      fetchData();
    } catch (e: any) {
      alert(errMsg(e));
    } finally {
      setSendingSignal(false);
    }
  };

  const handleStopSignal = async (id: string) => {
    if (!confirm('Stop this signal? New opportunities will no longer receive it.')) return;
    try {
      await api.stopAiTraderSignal(id);
      fetchData();
    } catch (e: any) {
      alert(errMsg(e));
    }
  };

  const handleSaveIncome = async (bot: BotRow) => {
    const value = Number(incomeEdit[bot.id]);
    if (!Number.isFinite(value) || value <= 0) { alert('Enter a positive amount'); return; }
    setSavingIncome(bot.id);
    try {
      await api.updateAiTraderBotReward(bot.id, value);
      setIncomeEdit(prev => { const next = { ...prev }; delete next[bot.id]; return next; });
      fetchData();
    } catch (e: any) {
      alert(errMsg(e));
    } finally {
      setSavingIncome(null);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', padding: '16px' }}>
      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Active Bots</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#10b981' }}>{stats.activeBots}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Total Bots</div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#3b82f6' }}>{stats.totalBots}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Total Locked</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#f59e0b' }}>${stats.totalLocked.toFixed(2)}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Total Rewards</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#10b981' }}>${stats.totalRewardsDistributed.toFixed(2)}</div>
          </div>
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Gifts Sent</div>
                <div style={{ fontSize: '18px', fontWeight: 800 }}>{stats.totalGiftsSent}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Gifts Volume</div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#8b5cf6' }}>${stats.totalGiftsAmount.toFixed(2)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modify Bot Balance */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Modify Bot Balance</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <input
              value={modUserQuery}
              onChange={e => { setModUserQuery(e.target.value); setSelectedUser(null); setModUserId(''); }}
              placeholder="@username, Telegram ID or User ID"
              autoCapitalize="off"
              style={inputStyle}
            />
            {userHits.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: '4px', background: '#0f1f38', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '8px', overflow: 'hidden' }}>
                {userHits.map(u => (
                  <button key={u.id} onClick={() => pickUser(u)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer', fontSize: '13px' }}>
                    <b>{u.firstName} {u.lastName || ''}</b> <span style={{ color: 'rgba(255,255,255,0.5)' }}>{u.username ? `@${u.username}` : ''} · {u.telegramUserId}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedUser && (
            <div style={{ fontSize: '12px', color: '#10b981' }}>
              Selected: {selectedUser.firstName} {selectedUser.username ? `@${selectedUser.username}` : ''} ({selectedUser.id.substring(0, 8)}…)
            </div>
          )}
          <input type="number" value={modAmount} onChange={e => setModAmount(e.target.value)} placeholder="Amount (+/-)" style={inputStyle} />
          <input value={modReason} onChange={e => setModReason(e.target.value)} placeholder="Reason" style={inputStyle} />
          <button onClick={handleModifyBalance} disabled={modifying || !modUserQuery.trim() || !modAmount || !modReason} style={{
            padding: '10px', borderRadius: '8px', border: 'none', background: '#f59e0b', color: '#000', fontWeight: 700,
            cursor: 'pointer', opacity: (modifying || !modUserQuery.trim() || !modAmount || !modReason) ? 0.5 : 1,
          }}>
            {modifying ? 'Processing...' : 'Modify Bot Balance'}
          </button>
        </div>
      </div>

      {/* Sub-view tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '4px' }}>
        {(['signal', 'bots', 'opportunities'] as SubView[]).map(v => (
          <button key={v} onClick={() => setSubView(v)} style={{
            flex: 1, padding: '8px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            background: subView === v ? 'rgba(59,130,246,0.2)' : 'transparent',
            color: subView === v ? '#3b82f6' : 'rgba(255,255,255,0.5)',
          }}>
            {v === 'bots' ? 'Bots & Income' : v === 'opportunities' ? 'Opps' : 'Signals'}
          </button>
        ))}
        <button onClick={fetchData} aria-label="Refresh" style={{ padding: '8px 12px', border: 'none', borderRadius: '10px', background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '14px' }}>↻</button>
      </div>

      {/* Bots Sub-view */}
      {subView === 'bots' && (
        <>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {(['all', 'active', 'pause', 'expired'] as BotFilter[]).map(f => (
              <button key={f} onClick={() => setBotFilter(f)} style={{
                padding: '6px 12px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                background: botFilter === f ? '#3b82f6' : 'rgba(255,255,255,0.08)',
                color: botFilter === f ? '#fff' : 'rgba(255,255,255,0.6)',
              }}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>Loading...</div>
          ) : bots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>No bots found</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {bots.map(bot => {
                const color = PKG_COLORS[bot.packageKey] || '#888';
                return (
                  <div key={bot.id} style={{ ...cardStyle, borderColor: `${color}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div>
                        <span style={{ fontWeight: 700, color }}>{PKG_NAMES[bot.packageKey] || bot.packageKey}</span>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginLeft: '8px' }}>${bot.rewardAmount.toFixed(5)}/opp</span>
                      </div>
                      <span style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 700,
                        color: STATUS_COLORS[bot.status] || '#888',
                        background: `${STATUS_COLORS[bot.status] || '#888'}18`,
                      }}>
                        {bot.status.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
                      @{bot.username} <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>({bot.userId.substring(0, 8)}...)</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px' }}>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Locked</span>
                        <div style={{ fontWeight: 700, color: '#f59e0b' }}>${bot.lockedBalance.toFixed(2)}</div>
                      </div>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Fee</span>
                        <div style={{ fontWeight: 700 }}>${bot.packageFee}</div>
                      </div>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Missed</span>
                        <div style={{ fontWeight: 700, color: bot.consecutiveMissed >= 3 ? '#ef4444' : '#fff' }}>{bot.consecutiveMissed}</div>
                      </div>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Perfect Days</span>
                        <div style={{ fontWeight: 700 }}>{bot.perfectDaysCount}</div>
                      </div>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Bonus</span>
                        <div style={{ fontWeight: 700, color: '#10b981' }}>${bot.perfectBonusTotal.toFixed(2)}</div>
                      </div>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>Gifts</span>
                        <div style={{ fontWeight: 700 }}>{bot.giftsSentThisMonth}/mo</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>
                      <span>{formatDate(bot.startedAt)} — {formatDate(bot.endsAt)}</span>
                      {bot.status === 'active' && <span>{bot.daysRemaining}d left</span>}
                    </div>
                    {bot.status !== 'expired' && (() => {
                      const editing = incomeEdit[bot.id] !== undefined;
                      const custom = bot.defaultRewardAmount !== null && Math.abs(bot.rewardAmount - bot.defaultRewardAmount) > 1e-6;
                      return (
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Income per opportunity (every {bot.opportunityInterval}h)</span>
                            <span style={{ fontWeight: 800, color: custom ? '#f59e0b' : '#10b981' }}>${bot.rewardAmount.toFixed(5)}</span>
                          </div>
                          {custom && (
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textAlign: 'right' }}>
                              Package default ${bot.defaultRewardAmount!.toFixed(5)}
                            </div>
                          )}
                          {editing ? (
                            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                              <input type="number" step="0.00001" min="0" value={incomeEdit[bot.id]} autoFocus
                                onChange={e => setIncomeEdit(prev => ({ ...prev, [bot.id]: e.target.value }))}
                                style={{ ...inputStyle, flex: 1, padding: '8px' }} />
                              <button onClick={() => handleSaveIncome(bot)} disabled={savingIncome === bot.id}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                                {savingIncome === bot.id ? '…' : 'Save'}
                              </button>
                              <button onClick={() => setIncomeEdit(prev => { const n = { ...prev }; delete n[bot.id]; return n; })}
                                style={{ padding: '8px 10px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' }}>
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                              <button onClick={() => setIncomeEdit(prev => ({ ...prev, [bot.id]: bot.rewardAmount.toFixed(5) }))}
                                style={{ flex: 1, padding: '7px', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>
                                Change income
                              </button>
                              {custom && (
                                <button onClick={() => { setIncomeEdit(prev => ({ ...prev, [bot.id]: bot.defaultRewardAmount!.toFixed(5) })); }}
                                  style={{ padding: '7px 10px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: '12px', cursor: 'pointer' }}>
                                  Reset to default
                                </button>
                              )}
                            </div>
                          )}
                          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' }}>
                            Applies from the next opportunity. An already open opportunity keeps its amount.
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Opportunities Sub-view */}
      {subView === 'opportunities' && (
        <>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {(['all', 'open', 'reward_pending', 'missed', 'rewarded', 'lost'] as OppFilter[]).map(f => (
              <button key={f} onClick={() => setOppFilter(f)} style={{
                padding: '5px 10px', borderRadius: '20px', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                background: oppFilter === f ? '#3b82f6' : 'rgba(255,255,255,0.08)',
                color: oppFilter === f ? '#fff' : 'rgba(255,255,255,0.6)',
              }}>
                {f === 'reward_pending' ? 'Pending' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>Loading...</div>
          ) : opps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>No opportunities found</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {opps.map(opp => {
                const color = PKG_COLORS[opp.packageKey] || '#888';
                return (
                  <div key={opp.id} style={{ ...cardStyle, borderColor: `${STATUS_COLORS[opp.status] || '#888'}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div>
                        <span style={{ fontWeight: 700, color, fontSize: '12px' }}>{PKG_NAMES[opp.packageKey] || opp.packageKey}</span>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginLeft: '6px' }}>@{opp.username}</span>
                      </div>
                      <span style={{
                        fontSize: '10px', padding: '2px 8px', borderRadius: '10px', fontWeight: 700,
                        color: STATUS_COLORS[opp.status] || '#888',
                        background: `${STATUS_COLORS[opp.status] || '#888'}18`,
                      }}>
                        {opp.status.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>Reward</span>
                      <span style={{ fontWeight: 700, color: opp.status === 'rewarded' ? '#10b981' : '#fff' }}>${opp.rewardAmount.toFixed(5)}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                      {formatDateTime(opp.opensAt)} — {formatDateTime(opp.expiresAt)}
                    </div>
                    {opp.signalPair && (
                      <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '4px' }}>
                        📊 {opp.signalPair} {opp.signalDirection} · Entry {opp.signalEntry} · TP {opp.signalTp} · SL {opp.signalSl}
                      </div>
                    )}
                    {(opp.status === 'open' || opp.status === 'reward_pending') && (
                      <button onClick={() => startSignal(opp)} style={{
                        marginTop: '8px', width: '100%', padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                        fontSize: '12px', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                      }}>
                        {opp.signalPair ? 'Edit Signal' : '+ Add Signal'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Signal Sub-view */}
      {subView === 'signal' && (
        <>
        <div style={cardStyle}>
          <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '4px' }}>{sigOppId ? 'Signal for one opportunity' : 'Publish Signal'}</div>
          {sigOppId ? (() => {
            const selected = opps.find(o => o.id === sigOppId);
            return (
              <div style={{ fontSize: '12px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px' }}>
                {selected ? `@${selected.username} · ${PKG_NAMES[selected.packageKey] || selected.packageKey} · ${selected.status.toUpperCase()}` : 'Selected opportunity'}
                <button onClick={resetSignalForm} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }}>cancel</button>
              </div>
            );
          })() : (
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '10px', lineHeight: 1.4 }}>
              The signal is shown to users right after they CATCH — in all currently open opportunities and in every new one until it expires. Publishing a new signal replaces the previous one for the same target.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {!sigOppId && (
              <>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Send to</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {[{ key: '', name: 'All bots' }, ...Object.entries(PKG_NAMES).map(([key, name]) => ({ key, name }))].map(p => (
                    <button key={p.key || 'all'} onClick={() => setSigPackage(p.key)} style={{
                      padding: '6px 10px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      background: sigPackage === p.key ? (PKG_COLORS[p.key] || '#3b82f6') : 'rgba(255,255,255,0.08)',
                      color: sigPackage === p.key ? '#fff' : 'rgba(255,255,255,0.6)',
                    }}>{p.name}</button>
                  ))}
                </div>
              </>
            )}
            <input value={sigPair} onChange={e => setSigPair(e.target.value)} placeholder="Pair (e.g. BTC/USDT)" style={inputStyle} />
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => setSigDirection('LONG')} style={{
                flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer',
                background: sigDirection === 'LONG' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                color: sigDirection === 'LONG' ? '#10b981' : 'rgba(255,255,255,0.5)',
              }}>LONG</button>
              <button onClick={() => setSigDirection('SHORT')} style={{
                flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer',
                background: sigDirection === 'SHORT' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
                color: sigDirection === 'SHORT' ? '#ef4444' : 'rgba(255,255,255,0.5)',
              }}>SHORT</button>
            </div>
            <input value={sigEntry} onChange={e => setSigEntry(e.target.value)} placeholder="Entry Price" style={inputStyle} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <input value={sigTp} onChange={e => setSigTp(e.target.value)} placeholder="Take Profit" style={{ ...inputStyle, flex: 1 }} />
              <input value={sigSl} onChange={e => setSigSl(e.target.value)} placeholder="Stop Loss" style={{ ...inputStyle, flex: 1 }} />
            </div>
            <input value={sigExchange} onChange={e => setSigExchange(e.target.value)} placeholder="Exchange URL (optional)" style={inputStyle} />
            {!sigOppId && (
              <>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Active for</div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {SIGNAL_DURATIONS.map(h => (
                    <button key={h} onClick={() => setSigDuration(h)} style={{
                      flex: 1, padding: '8px 0', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                      background: sigDuration === h ? '#3b82f6' : 'rgba(255,255,255,0.08)', color: sigDuration === h ? '#fff' : 'rgba(255,255,255,0.6)',
                    }}>{h}h</button>
                  ))}
                </div>
              </>
            )}
            {(() => {
              const disabled = sendingSignal || !sigPair.trim() || !sigEntry.trim() || !sigTp.trim() || !sigSl.trim();
              return (
                <button onClick={handleAddSignal} disabled={disabled} style={{
                  padding: '12px', borderRadius: '8px', border: 'none', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '14px',
                  background: disabled ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  color: '#fff', opacity: disabled ? 0.5 : 1,
                }}>
                  {sendingSignal ? 'Publishing...' : sigOppId ? 'Save Signal' : 'Publish Signal'}
                </button>
              );
            })()}
          </div>
        </div>

        <div style={{ fontSize: '13px', fontWeight: 700, margin: '16px 0 8px' }}>Published signals</div>
        {signals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>No signals yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {signals.map(sg => (
              <div key={sg.id} style={{ ...cardStyle, borderColor: sg.isActive ? 'rgba(16,185,129,0.35)' : 'rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 800 }}>
                    {sg.pair} <span style={{ color: sg.direction === 'LONG' ? '#10b981' : '#ef4444' }}>{sg.direction}</span>
                  </span>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', color: sg.isActive ? '#10b981' : '#6b7280', background: sg.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)' }}>
                    {sg.isActive ? 'ACTIVE' : 'ENDED'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Entry {sg.entry} · TP {sg.tp} · SL {sg.sl}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                  {sg.packageKey ? PKG_NAMES[sg.packageKey] || sg.packageKey : 'All bots'} · {sg.isActive ? 'until' : 'published'} {formatDateTime(sg.isActive ? sg.expiresAt : sg.createdAt)} · sent to {sg.appliedCount} opportunities
                </div>
                {sg.isActive && (
                  <button onClick={() => handleStopSignal(sg.id)} style={{ marginTop: '8px', width: '100%', padding: '7px', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>
                    Stop signal
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        </>
      )}
    </div>
  );
}
